<template>
  <div>
    <!-- XOL-626: `embedded` (RTS step 3's "Sharer Finance") renders the Sharer Agreement pane only,
         with the tab strip hidden. The site page is `embedded` false and renders as before. -->
    <el-tabs v-model="menu" :class="{'finance-sharer--embedded': embedded}">
      <el-tab-pane name="agreement">
        <template #label><span><i class="fa fa-file-signature text-muted"></i> Sharer Agreement</span></template>

        <el-row>
          <el-col :xs="24" :sm="12" :md="6">
            Agreement Start <strong>{{formatDate(details.started)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Expiry <strong>{{formatDate(details.expiry)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Term <strong v-if="term !== null">{{term}} months</strong><strong v-else>—</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Expires <strong>{{expiry !== null ? expiry : '—'}}</strong>
          </el-col>
        </el-row>

        <!-- XOL-462: strict_position_out_of_range / strict_term_exceeded banner.
             Deliberately OUTSIDE the <el-form> below — an alert inside an inline
             form reflows the input rows when it appears or disappears
             (ProjectTab.vue:12-17). -->
        <sharer-billing-issues :issues="billingIssues" />

        <!-- Vue's template parser closes a <p> at an <h3>, so the original `<p><h3>..buttons..</p>` has
             always rendered as an EMPTY <p>, then the heading and the two buttons as siblings, then
             (the stray `</p>`) a second empty <p>. The site page's DOM is pinned by
             FinanceSharerEmbedded.spec.js: a v-if has to wrap the heading and the buttons, and BOTH
             empty <p> stay. -->
        <p v-if="!embedded"></p>
        <template v-if="!embedded">
            <h3>Site Financials</h3>
            <!-- XOL-516 (Carlos, 2026-09-15): role set widened to add
                 `management` and `admin`. This DELIBERATELY REVERSES XOL-371
                 ruling A4-1 (2026-07-21), which set this to finance|project as
                 part of "revert over-relaxed Tower-Co gates". See the XOL-516
                 section in docs/plans/2026-07-21-xol-371-gating-review-by-url.md
                 for why that ruling was revisited. Do not "restore" it to the
                 July value without reading that. -->
            <el-button v-if="isManager('finance|project|management|admin') && !readOnly" type="text" aria-label="Edit sharer agreement" @click="editState()"><i :class="editing ? 'fa fa-check-circle text-success' : 'fa fa-edit'"></i></el-button>
            <el-button v-if="this.editing" type="text" aria-label="Cancel editing" @click="editing = false"><i class="fa fa-times-circle text-danger"></i></el-button>
        </template>
        <!-- ... and the stray `</p>` after the buttons has always produced a SECOND empty <p>: its 1em
             bottom margin is real spacing under the buttons (app.css zeroes only margin-top). -->
        <p v-if="!embedded"></p>

        <!-- XOL-626: in step 3 the heading replaces the icon toggles (the Edit / Cancel / Save
             buttons below take their place), and says where the hidden fields live. -->
        <h3 v-if="embedded" :id="'sharer-finance-heading-' + sharer.id">Rate and Fees</h3>
        <p v-if="embedded && editing" class="text-regular" :id="'sharer-finance-owned-' + sharer.id">Agreement dates, CAPEX and OPEX are changed in Modify Sharer. Status is changed on Site → Finance → Sharers.</p>

        <!-- XOL-467: any-to-all auto-fill notice. Outside <el-form> for the same
             reason as the billing-issues banner above — appearing/disappearing
             must never reflow the inline input rows. No Handover input on this
             tab, so the notice only ever concerns Agreement Start / Billing
             Start. -->
        <el-alert v-if="editing && autoFilled" type="info" show-icon :closable="false"
          description="Billing Start was blank, so it has been filled in from the date you just set. Adjust it if it differs." />

        <el-row>
          <el-col :span="24">
            <el-form inline label-position="top" ref='financeForm' :model='details' :key="formKey">

              <!-- XOL-626: the status, the dates and the sharer-specific CAPEX / OPEX are the sharer
                   form's in step 3 (D13), so they are HIDDEN here, not shown disabled: a disabled copy
                   would appear exactly when Edit is clicked and read as broken. The summary row above
                   keeps the dates in view, and the embedded payload never sends them. -->
              <el-row v-if="!embedded" :gutter="12">
                <el-col :span="24">
                  <select-sharer-status v-model="details.status" :disabled="!editing" />

                  <span v-if="details.started || editing" class="handover-field">
                    <date-input label="Agreement Start" v-model="details.started" :disabled="!editing"
                      ref="dateField_started"
                      :class="[{'licensee-date--editing': editing}, {'licensee-date--handover-gutter': showHandoverAction}]"
                      :error="fieldErrors.started" @change="onDateChange('started', $event)" />
                    <!-- XOL-479: this tab has no Handover input (Carlos, 2026-09-01 —
                         do not add one here). When the block/backstop names this field
                         as the one to fix, send the user to the only screen that CAN
                         fix it, without losing this in-progress edit (see
                         openHandoverOnProjectTab). Rendered only for the exact
                         handover-required message — never for the other `started`
                         messages, which this tab can resolve locally. -->
                    <el-button v-if="showHandoverAction" type="text" size="small"
                      class="handover-field__action" @click="openHandoverOnProjectTab">
                      Open Project tab to set Handover <i class="fa fa-arrow-right" aria-hidden="true"></i>
                    </el-button>
                  </span>
                  <date-input v-if="details.status !== 'Terminated' && (details.startBilling || editing)" label="Billing Start" v-model="details.startBilling" :disabled="!editing"
                    ref="dateField_startBilling" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.startBilling" @change="onDateChange('startBilling', $event)" />
                  <date-input v-if="details.status == 'Terminated' && (details.terminated || editing)" label="Terminated" v-model="details.terminated" :disabled="!editing"
                    :class="{'licensee-date--editing': editing}" @change="clearDateErrors" />
                  <date-input v-if="details.expiry || editing" label="Expiry" v-model="details.expiry" :disabled="!editing"
                    ref="dateField_expiry" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.expiry" @change="onDateChange('expiry', $event)" />

                </el-col>
              </el-row>

              <!-- Sharer Rate on its own row + Duplicate & modify (XOL-241) -->
              <!-- XOL-355: hidden when the rate is manually overridden — the tiered
                   sharer rate is irrelevant while a flat override is in effect. -->
              <!-- XOL-626 (S15 step 2, S18): a NON-manager in step 3 sees the rate as a read-only card: the
                   select would be blank (the by-org list is manager-only) and SharerRateVersion would toast
                   "Couldn't load the rate versions". While the request's rate read has not loaded (still
                   loading, failed, or the API routes are missing) the rate NAME and VERSION from the sharer
                   row stand in, as text. Hidden with the override (XOL-355), as the block it replaces. -->
              <el-row v-if="!details.overrideEnabled && showRateSummary" :gutter="12">
                <el-col :span="24">
                  <el-divider content-position="left">Sharer rate</el-divider>

                  <sharer-rate-card v-if="rateCardReady"
                    :rate="rateRead.rate" :version="rateRead.version" :position="rateRead.position" />

                  <template v-else>
                    <p class="finance-sharer__rate-summary text-regular">
                      <template v-if="sharer.rateName">
                        <span>Rate <strong>{{sharer.rateName}}</strong></span>
                        <span v-if="sharer.rateVersion">Version <strong>v{{sharer.rateVersion}}</strong></span>
                      </template>
                      <span v-else>No rate assigned to this sharer.</span>
                    </p>

                    <template v-if="sharer.rateName">
                      <p v-if="rateReadState === 'idle' || rateReadState === 'loading'" class="finance-sharer__rate-note text-regular"><i class="fa fa-spinner fa-spin text-primary" aria-hidden="true"></i> Loading the tier table…</p>
                      <p v-else-if="rateReadState === 'failed'" class="finance-sharer__rate-note text-regular">The tier table couldn't be loaded. <el-button type="text" size="small" @click="$emit('retry-rate-read')">Retry</el-button></p>
                    </template>
                  </template>
                </el-col>
              </el-row>

              <el-row v-if="!details.overrideEnabled && !showRateSummary" :gutter="12">
                <el-col :span="24">
                  <sharer-rate-override v-model="details.rate_id" :sharer-id="sharer.organisation_id" :disabled="!editing" @input="onRateChanged" />
                </el-col>
              </el-row>

              <!-- XOL-306: in-form version select (edit mode, version-managers only) -->
              <el-row v-if="editing && details.rate_id && canSwitchVersion && !details.overrideEnabled" :gutter="12">
                <el-col :sm="24" :md="16">
                  <el-form-item label="Rate version">
                    <!-- XOL-497 fold-in (Carlos, 2026-09-13): a licensee is always
                         pinned to a PUBLISHED version, so this control must not
                         offer or imply an unpinned state.

                         `clearable` removed. Clearing set selectedVersionId to
                         null, and on a rate change commitFinance() sends that
                         null explicitly — which API XOL-507 now rejects with a
                         422. The SPA was offering an action the API refuses.
                         Every option in the list is a rate_versions row, which
                         only publish() can create, so the options themselves
                         were never the problem.

                         The placeholder said "Latest (unversioned)", naming the
                         state we do not allow. It shows only for a legacy row
                         with no pin; "Latest (published)" names what such a row
                         resolves to and what a save will pin it to. -->
                    <select-rate-versions
                      :versions="rateVersions"
                      :value="selectedVersionId"
                      :loading="loadingVersions"
                      :disabled="!canSwitchVersion"
                      placeholder="Latest (published)"
                      @input="onSelectVersion" />
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- XOL-259 Phase 2 / XOL-306: read-only version summary (view mode) -->
              <!-- XOL-355: hidden when the rate is manually overridden. -->
              <el-row v-if="!details.overrideEnabled && !showRateSummary && (!embedded || canReadRateVersions)" :gutter="12">
                <el-col :span="24">
                  <el-divider content-position="left">Rate version</el-divider>
                  <sharer-rate-version v-show="!editing" ref="sharerRateVersion" :id="sharer.id" :rate-id="details.rate_id" />
                </el-col>
              </el-row>

              <!-- Transient note after a rate, version, status or fee-date change — XOL-306, XOL-695 -->
              <el-alert
                v-if="resumingFee"
                type="info"
                :closable="false"
                show-icon
                title=""
                description="Fees are recalculating in the background — this may take a moment." />

              <el-alert v-if="details && editing && !embedded" type="warning" title="Sharer specific!" description="Only input values PER sharer. For site wide CAPEX and OPEX see Finance Summary." show-icon  :closable="false" />
                <!-- XOL-319: vertical gap between the Rate version table and the Calculated (auto) rate -->
                <el-row :gutter="12" style="margin-top: 16px;">
                    <el-col :span="24">

                      <!-- XOL-268 Phase B: Current Fees + override controls -->
                      <currency-input
                        v-if="details.overrideEnabled"
                        label="Current Fees"
                        v-model="details.currentRate"
                        :disabled="!editing || !canOverride"
                        placeholder="Enter manual fee" />

                      <!-- Override checkbox — only visible when editing -->
                      <el-tooltip
                        v-if="editing"
                        content="Only finance, project, management or admin managers can override the rate."
                        placement="bottom"
                        :disabled="canOverride">
                        <check-box
                          v-if="editing"
                          class="override-lock-label"
                          :label="'Override/Lock\nRate'"
                          v-model="details.overrideEnabled"
                          :disabled="!canOverride" />
                      </el-tooltip>

                      <!-- Non-manager helper text (guaranteed visible fallback) -->
                      <p v-if="editing && !canOverride" class="text-muted" style="font-size:12px;margin:0 0 8px;">
                        Only finance, project, management or admin managers can override the rate.
                      </p>

                      <!-- Calculated (auto) rate display — XOL-268 -->
                      <el-form-item v-if="!details.overrideEnabled" label="Calculated (auto) rate">
                        <span v-if="details.calculatedRate !== null && details.calculatedRate !== undefined">{{calcRateDisplay}}</span>
                        <span v-else>
                          <span>—</span>
                          <span class="text-muted" style="font-size:12px;margin-left:6px;">No calculated rate yet (no rate set, no handover, or past the last tier).</span>
                        </span>
                      </el-form-item>

                      <!-- Transient note after clearing override — XOL-268 -->
                      <el-alert
                        v-if="resumingAuto"
                        type="info"
                        :closable="false"
                        show-icon
                        title=""
                        description="Resuming the calculated rate — this may take a moment to update." />

                      <float-input
                        v-if="details.discount || editing"
                        v-model="details.discount"
                        label="Discount" :prefix="details.discountPercentage ? currency : ''"
                        :suffix="details.discountPercentage ? '' : '%'"
                        :disabled="!editing" />

                        <check-box
                          v-if="editing || details.discount"
                          label="Disc. Type"
                          v-model="details.discountPercentage"
                          :disabled="!editing">
                          {{!details.discountPercentage ? '%' : currency}}
                        </check-box>

                        <float-input
                            v-if="details.interestRate || editing"
                            v-model="details.interestRate"
                            label="Interest Rate"
                            suffix="%"
                            :disabled="!editing" />

                      <float-input v-if="!embedded && (details.capex || editing)" label="Sharer Specific CAPEX" v-model="details.capex" :decimals="2" :disabled="!editing" />
                      <float-input v-if="!embedded && (details.opex || editing)" label="Sharer Specific OPEX" v-model="details.opex" :decimals="2" :disabled="!editing"  />
                </el-col>

              </el-row>

              <div v-if="details.updated_at">
                <small class="text-muted pull-right">Last update: <b>{{formatFriendlyDate(details.updated_at)}}</b><span v-if="details.updated_by"> by {{details.updated_by}}</span></small>
              </div>

          </el-form>

        </el-col>
      </el-row>

      <!-- XOL-626: the embedded form's Edit / Cancel / Save. Visible text on every button; Save is
           removed (Cancel stays) when the step freezes while editing. A non-manager, or a
           read-only form, gets no buttons. -->
      <el-row v-if="embedded">
        <el-col align="middle">
          <template v-if="!editing">
            <el-button class="btn-icon-gap" v-if="canEditEmbedded" ref="editButton" type="info" icon="fa fa-edit" size="mini" round
              :disabled="editBlocked !== null" :aria-describedby="editBlocked ? editBlockedId : null"
              @click="startEmbeddedEdit">Edit</el-button>
            <span v-if="canEditEmbedded && editBlocked" :id="editBlockedId" class="edit-blocked-hint text-regular">{{editBlocked}}</span>
          </template>

          <template v-else>
            <el-button class="btn-icon-gap" ref="cancelButton" type="warning" icon="fa fa-times-circle" size="mini" round :disabled="saving" @click="cancelEmbedded">Cancel</el-button>
            <el-button class="btn-icon-gap" v-if="!readOnly" ref="saveButton" type="success" icon="fa fa-check" size="mini" round :loading="saving"
              @click="saveEmbedded">Save</el-button>
          </template>
        </el-col>
      </el-row>

      <p v-if="embedded" class="text-regular finance-sharer__elsewhere">Invoices, V.O.s, billable equipment, the fee schedule and the fee timeline are on Site → Finance → Sharers.</p>

      <el-row v-if="!embedded && canAccess('finance') && sharer.site_id && sharer.id">
        <el-col :span="24">
          <files-panel title="License Agreement" type="site" :id="sharer.site_id" process="sharer" :process-id="sharer.id" hide-carousel />
        </el-col>
      </el-row>

   </el-tab-pane>


   <el-tab-pane v-if="!embedded" name="sharerInvoice" lazy>
     <template #label><span><i class="fa fa-money-check text-muted"></i> Sharer Invoice</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" />
   </el-tab-pane>

   <el-tab-pane v-if="!embedded" name="sharerVO" lazy>
     <template #label><span><i class="fa fa-screwdriver text-muted"></i> Sharer V.O.</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" is-vo />
   </el-tab-pane>


   <el-tab-pane v-if="!embedded" name="billableEquip" lazy>
     <template #label><span><i class="fa fa-tools text-muted"></i> Billable Equip.</span></template>
     <billable-equip-table :site-id="details.site_id" :site-data="siteData" :owner-id="sharer.organisation_id" />
   </el-tab-pane>

   <el-tab-pane v-if="!embedded" name="feeSchedule" lazy>
     <template #label><span><i class="fa fa-calendar-alt text-muted"></i> Fee Schedule</span></template>
     <!-- XOL-671: a new :key on each open re-creates the panel, so it never shows cached data.
          Not a v-if on menu: that blanked the lazy tab labels. -->
     <sharer-fee-schedule :key="'fee-schedule-' + feeScheduleOpens" :id="sharer.id" :started="details.started" />
   </el-tab-pane>

   <el-tab-pane v-if="!embedded" name="feeTimeline" lazy>
     <template #label><span><i class="fa fa-stream text-muted"></i> Fee Timeline</span></template>
     <!-- XOL-671: same as Fee Schedule above — re-created (and re-fetched) on every open. -->
     <sharer-fee-timeline :key="'fee-timeline-' + feeTimelineOpens" :id="sharer.id" :started="details.started" />
   </el-tab-pane>

 </el-tabs>
  </div>
</template>


<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'
import DateChainMixin from '@/mixins/DateChainMixin'
import DrawerDirtyMixin from '@/mixins/DrawerDirtyMixin'
import {dateChainErrors, autoFillDates} from '@/lib/licenseeDates'
import {toAppTime, appNow} from '@/lib/appTime'
import {pushDrawer} from '@/lib/layerNav'


import {settings} from '@/components/Data'

import SelectSharerStatus from '@/components/atoms/selects/SelectSharerStatus'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'

import BillableEquipTable from '@/components/assets/subcat/BillableEquipTable'

import SiteInvoice from '@/components/sites/finance/SiteInvoice'
import SharerFeeSchedule from './SharerFeeSchedule'
import SharerFeeTimeline from './SharerFeeTimeline'
import SharerRateVersion from './SharerRateVersion'
import SharerRateCard from './SharerRateCard'
import SharerBillingIssues from './SharerBillingIssues'

// XOL-479: single source of truth for the handover-required copy — used by
// both the client-side block (dateChainCopy) and the 422 backstop
// (onSaveErrors), and compared against by `showHandoverAction` to decide
// whether the "open Project tab" affordance renders. Do NOT add a Handover
// input to this tab (Carlos, 2026-09-01) — the fix lives on the Project tab;
// this only gets the user there without losing their edit.
var HANDOVER_REQUIRED_MESSAGE = 'This sharer has no Handover date — set it on the Project tab before saving a date change.'

// XOL-467 (any-to-all widening): Agreement Start <-> Billing Start only —
// this tab has no Handover input, so Handover is neither source nor target
// here (Carlos, 2026-09-01). See licenseeDates.js's autoFillDates() doc.
var DATE_CHAIN_FIELDS = ['started', 'startBilling']

// XOL-695: the dates (besides status) whose change makes the API queue a fee recompute. Mirrors
// xolas-tms-api SharerController::REQUEST_EDIT_FEE_DATES. Handover is not a fee input.
var FEE_INPUT_DATES = ['started', 'startBilling', 'expiry']

// XOL-626: the ONLY keys the embedded (RTS step 3) bulk PUT may carry. The sharer form owns
// everything else. `rate_version_id` and `unassign_rate` are conditional, by commitFinance's
// existing rules; `actionStepId` is the step context.
var EMBEDDED_PAYLOAD_KEYS = [
  'site_id', 'organisation_id', 'rate_id',
  'discount', 'discountPercentage', 'interestRate',
  'rate_version_id', 'unassign_rate'
]

export default {
  mixins: [DateMixins, NumberMixins, PostMixins, DateChainMixin, DrawerDirtyMixin],

  props: {
    sharer: {
      type: Object,
      required: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    // XOL-626: RTS step 3's "Sharer Finance". Renders the Sharer Agreement pane only, owns
    // rate / version pin / override / discount / interest, never sends the fields the sharer
    // form owns, stamps every write with the step context, and runs its own Edit / Cancel / Save.
    embedded: {
      type: Boolean,
      default: false
    },

    // The step-3 party-1 row id, sent with every write so a completed step refuses it (D10).
    actionStepId: {
      type: Number,
      default: null
    },

    // One editor at a time: why Edit is disabled. null = not blocked, '' = blocked with no hint,
    // else the hint text (set by step 3).
    editBlocked: {
      type: String,
      default: null
    },

    // XOL-626 (S15): the request's rate read (GET licensees/action/{id}/rate, normalised), passed by
    // step 3 only once it has loaded; null otherwise. A non-manager sees it as a card.
    rateRead: {
      type: Object,
      default: null
    },

    // idle | loading | loaded | failed | unavailable: which fallback line a non-manager sees while
    // `rateRead` is null.
    rateReadState: {
      type: String,
      default: 'idle'
    }
  },

  components: {
    BillableEquipTable,
    SelectSharerStatus,
    SelectRateVersions,
    SiteInvoice,
    SharerRateOverride,
    SharerFeeSchedule,
    SharerFeeTimeline,
    SharerRateVersion,
    SharerRateCard,
    SharerBillingIssues,
  },

  computed: {
    // XOL-626. The three manager reads, narrowest first:
    //   canReadRateVersions  GET /licensees/{id}/rate-versions and /rates/sharer/by-org/{orgId}
    //                        (api-rates.php:35-37): finance|admin|management, NOT project
    //   canEditEmbedded      the edit toggle (and canOverride / canSwitchVersion): + project
    canReadRateVersions: function () {
      return this.isManager('finance|admin|management')
    },

    // A non-manager in step 3 reads the rate as text (see the template); a project manager does
    // too until they Edit, when they get the select (a gap the site page already has).
    showRateSummary: function () {
      return this.embedded && !this.editing && !this.canReadRateVersions
    },

    // The non-manager card renders when the read has loaded (otherwise the fallback line stands in).
    rateCardReady: function () {
      return this.showRateSummary && !!this.rateRead
    },

    canEditEmbedded: function () {
      return this.embedded && this.isManager('finance|project|management|admin') && !this.readOnly
    },

    editBlockedId: function () {
      return 'sharer-finance-edit-blocked-' + this.sharer.id
    },

    // The seven owned values differ from what they were when Edit was clicked.
    financeModified: function () {
      var s = this.snapshot

      if (!s) return false

      var d = this.details
      var num = function (v) { return (v === null || v === undefined || v === '') ? null : Number(v) }

      return (d.rate_id || null) !== (s.rate_id || null) ||
        (this.selectedVersionId !== null && this.selectedVersionId !== s.selectedVersionId) ||
        !!d.overrideEnabled !== !!s.overrideEnabled ||
        num(d.currentRate) !== num(s.currentRate) ||
        num(d.discount) !== num(s.discount) ||
        !!d.discountPercentage !== !!s.discountPercentage ||
        num(d.interestRate) !== num(s.interestRate)
    },

    // The close-time dirty check (LayerHost). Always false on the site page.
    embeddedDirty: function () {
      return this.embedded && this.editing && this.financeModified && !this.saving
    },

    // Both are rendered unconditionally in the header, and `expiry` is
    // genuinely optional (line 42 gates its own input on `details.expiry ||
    // editing`). toAppTime returns null where the old moment(null) returned an
    // *invalid* moment — which absorbed .diff()/.fromNow() as NaN/'Invalid
    // date' — so these must guard or the whole tab throws.
    term: function () {
      var oExpiry = toAppTime(this.details.expiry)
      var oStarted = toAppTime(this.details.started)
      return (oExpiry === null || oStarted === null) ? null : oExpiry.diff(oStarted, 'months')
    },

    expiry: function () {
      var oExpiry = toAppTime(this.details.expiry)
      return (oExpiry === null) ? null : oExpiry.from(appNow())
    },

    // XOL-371 review 2026-07-21 (A4-2) set this to finance|project, matching the
    // edit-toggle gate. XOL-516 (Carlos, 2026-09-15) widens all three of this
    // component's gates together to add management|admin — kept identical on
    // purpose, because three sibling gates with two role sets is precisely how
    // the mismatch XOL-516 exists to fix came about.
    canOverride: function () {
      return this.isManager('finance|project|management|admin') && !this.readOnly
    },

    // XOL-259 Phase 2 / XOL-371 A4-3. Widened by XOL-516 with A4-1 and A4-2 —
    // same role set as canOverride and the edit toggle.
    //
    // This is the gate XOL-516 was filed about: the Min alert on the rate form
    // tells the reader to re-pin, and that form is gated
    // isManager('finance|management|admin'). With this set, every reader of
    // that alert can now reach the control it names.
    canSwitchVersion: function () {
      return this.isManager('finance|project|management|admin') && !this.readOnly
    },

    // XOL-268: formatted calculated rate, dash when null/undefined
    calcRateDisplay: function () {
      var v = this.details.calculatedRate
      if (v === null || v === undefined) {
        return '—'
      }
      return this.formatMoney(v, 2)
    },

    // XOL-479: gates the "Open Project tab to set Handover" affordance —
    // exact-match against the shared constant so it never lights up for a
    // DIFFERENT message that happens to also land on `started`
    // (started_required).
    showHandoverAction: function () {
      return this.fieldErrors.started === HANDOVER_REQUIRED_MESSAGE
    }
  },

  methods: {
    // XOL-462: modelled on ProjectTab.loadBillingMode() — a silent side-read.
    // Never invent a clean state from a failed fetch: leave billingIssues at
    // whatever it already was (data() seeds []).
    loadBillingIssues: function () {
      if (!this.details.id) return
      var self = this
      this.fetchData(
        'licensees/' + this.details.id + '/fee-schedule',
        function (data) {
          var meta = data && data.meta
          self.billingIssues = (meta && Array.isArray(meta.issues)) ? meta.issues : []
        },
        'billing issues',
        function () { /* leave empty — never invent a clean state from a failed fetch */ }
      )
    },

    // XOL-268: derive overrideEnabled — prefer the explicit field, fall back to rateLocked
    deriveOverride: function (obj) {
      if (obj && obj.overrideEnabled !== undefined && obj.overrideEnabled !== null) {
        return !!obj.overrideEnabled
      }
      return !!(obj && obj.rateLocked)
    },

    // XOL-373: every licensee write on this screen must re-read the audit stamp.
    //
    // The write responses differ in shape — the override calls and the re-pin
    // return `decorateSharer($lic->fresh())`, while `update()`'s default branch
    // returns the raw model (`SharerController.php:840`) — but ALL of them carry
    // `updated_at`/`updated_by`, which is all this needs.
    //
    // Only two of the four handlers folded them, so setting or clearing an
    // override left the "Last update … by X" line (rendered at the FOOT of the
    // form, and gated only on `updated_at`, so it shows whether or not you are
    // editing) crediting the PREVIOUS editor with the previous date.
    //
    // The fold itself lives in PostMixins so this screen and the Project/Utility
    // tabs share one definition — the gap arose from those lines being inlined
    // in some handlers and absent from others.
    applyStamp: function (data) {
      this.applyAuditStamp(this.details, data)
    },

    // XOL-268: fold response from PUT /rate-override into local state + reset baselines
    applyOverrideResponse: function (data) {
      if (!data) return
      if (data.currentRate !== undefined) {
        this.details.currentRate = data.currentRate
      }
      if (data.calculatedRate !== undefined) {
        this.details.calculatedRate = data.calculatedRate
      }
      var derived = this.deriveOverride(data)
      this.details.overrideEnabled = derived
      this.originalRate = data.currentRate
      this.originalOverride = derived
      // XOL-373: an override IS an edit — attribute it.
      this.applyStamp(data)
    },

    // XOL-268: PUT /licensees/{id}/rate-override
    saveRateOverride: function () {
      var self = this

      // Guard: must have a valid positive rate when override is enabled
      var rate = Number(this.details.currentRate)
      if (!this.details.currentRate || isNaN(rate) || rate <= 0) {
        this.$message({
          type: 'warning',
          title: 'Missing fee',
          message: 'Enter a Current Fee amount before overriding the calculated rate.'
        })
        this.editing = true
        return false
      }

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      var body = { currentRate: rate }

      if (this.embedded && this.actionStepId) {
        body.actionStepId = this.actionStepId
      }

      var token = this.writeBegin()

      this.settleOnPromise(token, this.send(
        self,
        url,
        body,
        'Rate override',
        function (data) {
          self.applyOverrideResponse(data)
          self.$emit('saved', data)
          self.settleWrite(token, true)
        },
        this.embedded ? function (err) { self.embeddedWriteFailed(err, token) } : null,
        axios.put,
        !this.embedded,
        'Save',
        null,
        this.embedded
      ))
      return true
    },

    // XOL-268: DELETE /licensees/{id}/rate-override
    clearRateOverride: function () {
      var self = this

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      // XOL-626 (D10): a DELETE carries its step context in the BODY, which axios takes as
      // `config.data`: `send` calls formAction(url, formData), and axios.delete(url, config) would
      // read the second argument as the CONFIG, so the context would be lost and the lock would
      // silently not apply. `scopeOf` reads formData.actionStepId. Off the request, null / axios.delete.
      var step = this.embedded && this.actionStepId
      var deleteWithBody = function (u, body) { return axios.delete(u, { data: body }) }

      var token = this.writeBegin()

      this.settleOnPromise(token, this.send(
        self,
        url,
        step ? { actionStepId: this.actionStepId } : null,
        'Rate override',
        function (data) {
          self.details.overrideEnabled = false
          self.originalOverride = false
          self.resumingAuto = true
          // Do NOT copy stale data.currentRate — let calculatedRate govern display
          if (data && data.calculatedRate !== undefined) {
            self.details.calculatedRate = data.calculatedRate
          }
          // XOL-373: clearing an override is an edit too. This path has its own
          // handler rather than applyOverrideResponse (which would copy the
          // stale currentRate the comment above refuses), so the stamp is
          // folded here explicitly — it is NOT reached via that method.
          self.applyStamp(data)
          self.$emit('saved', data)
          self.settleWrite(token, true)
        },
        this.embedded ? function (err) { self.embeddedWriteFailed(err, token) } : null,
        step ? deleteWithBody : axios.delete,
        !this.embedded,
        'Save',
        null,
        this.embedded
      ))
    },

    // XOL-306: load published versions for a rate; autoLatest=true pins selectedVersionId
    // to the current version automatically (on rate change), autoLatest=false seeds
    // the existing pin from originalVersionId (on edit-open).
    loadVersionsForRate: function (rateId, autoLatest) {
      var self = this
      // Only version-managers manage the pin; the /rates/{id}/versions endpoint is
      // gated finance|admin|management, so skip the fetch for finance|project-only
      // editors (avoids a 403 + spurious error toast). Their save omits
      // rate_version_id (see commitFinance) so the backend auto-pins on rate change.
      if (!this.canSwitchVersion) {
        this.rateVersions = []
        this.selectedVersionId = this.originalVersionId
        return
      }
      if (!rateId) {
        this.rateVersions = []
        this.selectedVersionId = null
        return
      }
      this.loadingVersions = true
      this.fetchData(
        'rates/' + rateId + '/versions',
        function (data) {
          self.rateVersions = Array.isArray(data) ? data : []
          if (autoLatest) {
            var current = self.rateVersions.find(function (v) { return v.is_current })
            self.selectedVersionId = current ? current.id : null
          } else {
            self.selectedVersionId = self.originalVersionId
          }
          self.loadingVersions = false
        },
        'rate versions',
        function (_err) {
          self.rateVersions = []
          self.selectedVersionId = autoLatest ? null : self.originalVersionId
          self.loadingVersions = false
        }
      )
    },

    // XOL-306: called by @input on sharer-rate-override; auto-defaults version to latest
    onRateChanged: function (rateId) {
      this.loadVersionsForRate(rateId, true)
    },

    // XOL-306: called by @input on select-rate-versions in edit mode
    onSelectVersion: function (id) {
      this.selectedVersionId = (id === undefined || id === null) ? null : id
    },

    // XOL-268 Phase B: extracted write sequence — receives already-computed booleans
    commitFinance: function (overrideChanged, rateChanged, rateIdChanged, versionPinNeeded, clearingRate) {
      if (this.details.overrideEnabled && (overrideChanged || rateChanged)) {
        if (this.saveRateOverride() === false) return  // empty-fee guard: keep editing, no bulk save
      } else if (!this.details.overrideEnabled && overrideChanged) {
        this.clearRateOverride()
      }
      var self = this
      var payload = this.clone(this.details)
      delete payload.currentRate
      delete payload.rateLocked
      // XOL-416: this payload is a blocklist off the full decorateSharer body, so it
      // echoed the sharer's billing slot back like the sharer tabs did. The slot is
      // not editable on this screen, so it is not sent.
      //
      // This is a real guard, not tidiness: it is what stops a RATE edit from moving
      // a sharer's BILLING POSITION as a side effect. Deleting the line would let any
      // stale `sharerNo` in `details` ride along on a save that has nothing to do
      // with it.
      //
      // (XOL-431 removed the neighbouring `delete payload.billing_no`. That column no
      // longer exists, so it stripped a key that was never in the body.)
      delete payload.sharerNo
      // XOL-306/XOL-312: the bulk PUT auto-pins ONLY when rate_id changes — send the
      // version-manager's chosen pin on a rate change so it's atomic + honoured.
      // A same-rate version switch is ignored by the bulk PUT, so it's persisted
      // via the dedicated re-pin endpoint in the success callback.
      // (XOL-516: the `canSwitchVersion &&` guards below are now unreachable-false
      // — the edit toggle and canSwitchVersion are the same predicate, so anyone
      // reaching commitFinance has it. Kept as defence, not as a live branch.)
      //
      // XOL-497: the id is sent ONLY when we actually have one. "null = unpin"
      // is no longer a supported operation — API XOL-507 refuses an explicit
      // null with a 422 on a rate change, and every licensee is pinned.
      // selectedVersionId can still be null here without the user asking for
      // it: the versions GET failing sets it null, and a legacy licensee with
      // no pin starts null. Sending that would 422 a save the user cannot fix
      // from this form. Omitting instead lets the API auto-pin is_current,
      // which is the intended outcome in both cases.
      delete payload.rate_version_id
      if (this.canSwitchVersion && rateIdChanged && this.selectedVersionId) {
        payload.rate_version_id = this.selectedVersionId
      }
      // XOL-514 (paired with API XOL-513): removing a licensee's rate stops its
      // billing, and the API used to accept that silently with a 200. It now
      // refuses a rate-clear on a licensee that HAS a rate unless the payload
      // says the removal was intended.
      //
      // Sent on every clear, including the overridden case. The flag answers
      // "did the user mean to remove the rate", which is true either way; it is
      // only the CONSEQUENCE that differs by override state, and that
      // difference lives in the confirm copy, not here.
      //
      // Normalising rate_id to null is not cosmetic: Element's clearable
      // el-select emits '', so without this the body carries `rate_id: ""` and
      // the clear only works because Laravel's ConvertEmptyStringsToNull
      // rewrites it upstream of validation. Sending what we mean removes the
      // dependency on that middleware staying in the stack.
      if (clearingRate) {
        payload.rate_id = null
        payload.unassign_rate = true
      }
      // XOL-467: no Handover input on this tab — never echo it back.
      // Sending it unconditionally would re-arm LicenseeRequest's row-level
      // chain invariant on every unrelated save (a pure rate change), which
      // is exactly the lockout the dirty-gate exists to remove for the ~72
      // Active licensees that already violate it. `started` / `startBilling`
      // / `expiry` are dirty-gated too — sent only when actually edited.
      delete payload.handover
      if (this.details.started === this.originalStarted) delete payload.started
      if (this.details.startBilling === this.originalStartBilling) delete payload.startBilling
      if (this.details.expiry === this.originalExpiry) delete payload.expiry

      // XOL-626: in step 3 the payload is an ALLOW-LIST, not a blocklist off the whole decorated
      // row. The sharer form owns identity, dates, CAPEX / OPEX and the Sharer No.; this form owns
      // the rate, the pin, the override, the discount and the interest. Disjoint payloads are what
      // stop one form's stale value overwriting the other's save.
      if (this.embedded) {
        payload = this.embeddedPayload(payload, clearingRate)
      }

      // XOL-695: a status or fee-date change makes the API queue a recompute, like a rate or
      // version change does. Read off the FINAL payload: the date keys are dirty-gated above, so a
      // key present means that date changed, and the embedded allow-list drops status and dates,
      // so this is false there.
      var has = function (k) { return Object.prototype.hasOwnProperty.call(payload, k) }
      var feeInputsChanged = FEE_INPUT_DATES.some(has) || (has('status') && payload.status !== this.originalStatus)

      var token = this.writeBegin()

      this.settleOnPromise(token, this.saveForm(this, '/licensees', this.details.id, payload, 'Sharer Financial Data', function (resp) {
        if (rateIdChanged) {
          self.originalRateId = self.details.rate_id
        }
        self.originalStarted = self.details.started
        self.originalStartBilling = self.details.startBilling
        self.originalExpiry = self.details.expiry
        self.originalStatus = self.details.status
        self.autoFilled = false
        // Same-rate version change → the bulk PUT ignores rate_version_id when the
        // rate is unchanged (XOL-312), so persist it via the dedicated re-pin
        // endpoint (XOL-259). (Same-rate unpin to null isn't persistable — the
        // endpoint requires an id — so that edge is skipped.)
        if (self.canSwitchVersion && !rateIdChanged && versionPinNeeded && self.selectedVersionId !== null) {
          self.repinVersion(self.selectedVersionId)
        } else {
          self.originalVersionId = self.selectedVersionId
          if (resp) self.onVersionRepinned(resp)
          // Reload the read-only summary after a rate change (its pin/rate moved);
          // a no-op save needs no refresh.
          if (rateIdChanged && self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        }
        // Transient fee-recalc note when rate, version, status or a fee date changed (async recompute)
        if (rateIdChanged || versionPinNeeded || feeInputsChanged) {
          self.resumingFee = true
          // XOL-462: a rate or version change can create or clear a strict
          // violation — re-read the issues banner. (XOL-695: so can a date or status change.)
          self.loadBillingIssues()
        }
        self.$emit('saved', resp)
        self.settleWrite(token, true)
      }, function (errors) {
        // XOL-479: 422 backstop — see onSaveErrors().
        self.onSaveErrors(errors)
        if (self.embedded) self.embeddedWriteFailed(errors, token)
      }, !this.embedded, null, this.embedded))

      // Step 3 stays in edit mode until EVERY write of this Save has answered (settleWrite), so a
      // failed save never leaves unsaved values showing as if they were saved.
      if (!this.embedded) {
        this.editing = false
      }
    },

    // XOL-626. The embedded bulk-PUT body: only the keys this form owns, plus the step context.
    // `payload` is the blocklist body commitFinance already built, so rate_version_id / unassign_rate
    // (and the rate_id: null that goes with a clear) keep their existing rules.
    embeddedPayload: function (payload, clearingRate) {
      var out = {}

      EMBEDDED_PAYLOAD_KEYS.forEach(function (key) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          out[key] = payload[key]
        }
      })

      if (clearingRate) {
        out.rate_id = null
        out.unassign_rate = true
      }

      if (this.actionStepId) {
        out.actionStepId = this.actionStepId
      }

      return out
    },

    // -- XOL-626: the embedded Save is several writes (override PUT / DELETE, the bulk PUT, a
    // re-pin). The form stays in edit mode, with `saving` on, until all of them answered.
    //
    // Each write has a TOKEN, and settles exactly once. send() runs no callback for a 2xx that is not a
    // 200, or for a non-200 body that checkWarning swallows, so a write can answer without either
    // callback running; `settleOnPromise` settles it (as failed: nothing was confirmed) when send()'s
    // promise resolves and the callbacks did not.
    writeBegin: function () {
      if (!this.embedded) return null

      this.pendingWrites++
      this.saving = true

      return { done: false }
    },

    settleWrite: function (token, ok) {
      if (!token || token.done) return

      token.done = true

      if (ok) {
        this.savedSinceEdit = true
      } else {
        this.writesFailed = true
      }

      this.pendingWrites = Math.max(0, this.pendingWrites - 1)

      if (this.pendingWrites === 0) {
        this.finishEmbeddedSave()
      }
    },

    settleOnPromise: function (token, promise) {
      if (!token || !promise || typeof promise.then !== 'function') return

      var self = this
      var late = function () { self.settleWrite(token, false) }

      promise.then(late, late)
    },

    // A write was refused or failed. A completed step is reported in the request drawer's C-12
    // card (the failure callback is the only thing that hears it); anything else already toasted.
    embeddedWriteFailed: function (err, token) {
      this.stepCompletedRefusal(err, 'sharerFinance', { actionStepId: this.actionStepId })
      this.settleWrite(token, false)
    },

    finishEmbeddedSave: function () {
      this.saving = false

      var self = this

      if (this.writesFailed) {
        // Stay in edit mode with the user's values. If focus left the form, put it on Save.
        this.writesFailed = false
        this.$nextTick(function () {
          if (self.$el && !self.$el.contains(document.activeElement)) self.focusButton('saveButton')
        })
        return
      }

      this.editing = false
      this.snapshot = null
      this.savedSinceEdit = false
      this.$message({ type: 'success', message: 'Sharer finance changes saved.' })
      this.requestEditFocus()
    },

    // Back in read mode: focus goes to Edit as soon as it exists and is enabled. Right after a save step 3
    // re-reads the sharer and `editBlocked` is '' (Edit disabled) for a moment, and a disabled button
    // cannot take focus, so the request waits for the editBlocked watcher.
    requestEditFocus: function () {
      var self = this

      this.wantEditFocus = true
      this.$nextTick(function () { self.tryFocusEdit() })
    },

    tryFocusEdit: function () {
      if (!this.wantEditFocus) return

      var b = this.$refs.editButton
      var el = b && (b.$el || b)

      if (!el || el.disabled) return

      this.wantEditFocus = false
      el.focus()
    },

    focusButton: function (ref) {
      var b = this.$refs[ref]
      var el = b && (b.$el || b)

      if (el && el.focus) el.focus()
    },

    // Edit: keep the seven owned values, so Cancel restores them and an Edit that changed nothing
    // is not "dirty". `selectedVersionId` is snapshotted as the PIN (originalVersionId), because the
    // select only receives it once the versions list lands.
    startEmbeddedEdit: function () {
      if (this.editing || !this.canEditEmbedded) return

      this.editState()

      // The version select receives its pin only when the versions list lands (loadVersionsForRate,
      // async). Until then selectedVersionId is null and differs from the pin, so a quick Save would
      // show a "Confirm fee change" with NO bullets (versionIdChanged). Seed it now: the list
      // callback sets the same value.
      this.selectedVersionId = this.originalVersionId
      this.savedSinceEdit = false

      this.snapshot = {
        rate_id: this.details.rate_id,
        selectedVersionId: this.originalVersionId,
        overrideEnabled: this.details.overrideEnabled,
        currentRate: this.details.currentRate,
        discount: this.details.discount,
        discountPercentage: this.details.discountPercentage,
        interestRate: this.details.interestRate
      }

      var self = this
      this.$nextTick(function () {
        // The first enabled TEXT input: an el-select's input is readonly, and focusing it would open
        // its dropdown over the rows below.
        var input = self.$el && self.$el.querySelector('.el-form input:not([disabled]):not([readonly])')
        if (input) input.focus()
      })
    },

    cancelEmbedded: function () {
      var s = this.snapshot

      // A PARTIAL failure (the override PUT went through, the bulk PUT did not): the server already holds
      // part of this Save, so the snapshot is no longer "what is saved". Restoring it would show the old
      // values over a new override. Tell step 3 to re-read (`resync`) and rebuild from the fresh row when
      // it arrives (the `sharer` watcher).
      if (this.savedSinceEdit) {
        this.savedSinceEdit = false
        this.resyncPending = true
        this.snapshot = null
        this.editing = false
        this.$emit('resync')
        this.requestEditFocus()
        return
      }

      if (s) {
        this.details.rate_id = s.rate_id
        this.details.overrideEnabled = s.overrideEnabled
        this.details.currentRate = s.currentRate
        this.details.discount = s.discount
        this.details.discountPercentage = s.discountPercentage
        this.details.interestRate = s.interestRate
        this.selectedVersionId = s.selectedVersionId
      }

      this.snapshot = null
      // The select atoms copy their `value` in data(): remount the form so they show the restored values.
      this.formKey++
      this.editing = false

      this.requestEditFocus()
    },

    // Rebuild the form's state from a freshly read row (the same as data() does at mount).
    resyncFromSharer: function () {
      var derived = this.deriveOverride(this.sharer)

      this.details = Object.assign(this.clone(this.sharer), { overrideEnabled: derived })
      this.originalRate = this.sharer.currentRate
      this.originalOverride = derived
      this.originalRateId = this.sharer.rate_id
      this.originalVersionId = (this.sharer.rate_version_id != null && this.sharer.rate_version_id !== '') ? Number(this.sharer.rate_version_id) : null
      this.originalStarted = this.sharer.started
      this.originalStartBilling = this.sharer.startBilling
      this.originalExpiry = this.sharer.expiry
      this.originalStatus = this.sharer.status
      this.selectedVersionId = null
      this.rateVersions = []
      this.resumingFee = false
      this.resumingAuto = false
      this.formKey++
    },

    // Save: nothing changed -> no request and no message; changed -> the existing save path
    // (validation, the fee-change confirms, then commitFinance).
    saveEmbedded: function () {
      if (this.saving) return

      if (!this.financeModified) {
        this.editing = false
        this.snapshot = null

        this.requestEditFocus()
        return
      }

      this.editState()
    },

    // XOL-306: persist a SAME-RATE version switch via the dedicated re-pin endpoint
    // (the bulk PUT only auto-pins on a rate change — XOL-312). Mirrors the former
    // SharerRateVersion.doSwitch; folds the decorated licensee + refreshes the summary.
    repinVersion: function (versionId) {
      var self = this
      var body = { rate_version_id: versionId }

      if (this.embedded && this.actionStepId) {
        body.actionStepId = this.actionStepId
      }

      var token = this.writeBegin()

      this.settleOnPromise(token, this.send(
        this,
        this.getEndpoint('/licensees/' + this.details.id + '/rate-version'),
        body,
        'Rate version',
        function (licensee) {
          self.originalVersionId = versionId
          if (licensee) self.onVersionRepinned(licensee)
          if (self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
          self.$emit('saved', licensee)
          self.settleWrite(token, true)
        },
        function (err) {
          // error toast handled by send; the read-only summary reflects server truth
          if (self.embedded) self.embeddedWriteFailed(err, token)
        },
        axios.put,
        !this.embedded,
        'Save',
        null,
        true
      ))
    },

    // XOL-268 Phase B: compose the $confirm body from the applicable change bullets
    buildImpactMessage: function (overrideOn, overrideOff, rateChange, versionChange, clearingRate) {
      var items = []
      if (overrideOn) {
        items.push('<li>This sharer’s fee will be locked at the manual amount and the scheduled rate recalculation will skip this sharer until the override is removed.</li>')
      }
      if (overrideOff) {
        items.push('<li>This sharer’s fee will return to the calculated (automatic) rate and will recompute on its own — this may take a moment to appear.</li>')
      }
      if (rateChange) {
        if (clearingRate) {
          // XOL-514: clearing the rate is not a rate change, it is a STOP.
          // The generic bullet below ("calculated from the new rate's tiers")
          // describes a rate that no longer exists, so it must not fire here.
          //
          // Verified in the API rather than assumed, because this is billing
          // copy: RecalculateSiteSharerFees skips a licensee when
          // `is_null($rateId) && ! $licensee->rateLocked` — logging "no
          // rate_id — skipping, leaving currentRate untouched". So the fee
          // amount does NOT reset to zero; it freezes at whatever was last
          // computed, and no further fee rows are generated.
          //
          // The `! rateLocked` half of that condition is why this bullet is
          // gated on the override being OFF. An overridden licensee is
          // rateless-but-locked and keeps billing its frozen amount through
          // the locked ledger branch, so "stops being billed" would be false.
          // Today the rate picker only renders when the override is off
          // (template `v-if="!details.overrideEnabled"` on its row), but the
          // user can clear the rate and THEN tick Override in the same edit —
          // which is exactly the combination this guard covers. If the picker
          // is ever shown while overridden, re-check this bullet.
          if (this.details.overrideEnabled) {
            items.push('<li>This sharer’s rate will be removed. Its fee stays locked at the manual amount above, so billing continues.</li>')
          } else {
            items.push('<li><strong>This sharer will stop being billed.</strong> With no rate, no further fees are generated for it.</li>')
            items.push('<li>Its current fee amount is left as it is — it does not reset to zero — and past records are not affected.</li>')
            items.push('<li>Billing resumes when a rate is set again.</li>')
          }
        } else {
          items.push('<li>This sharer’s fees will be calculated from the new rate’s tiers going forward; past records are not affected.</li>')
        }
      }
      if (versionChange) {
        if (this.selectedVersionId) {
          items.push('<li>This sharer will be pinned to a specific rate version; future fees use that version’s frozen tiers and recompute in the background — this may take a moment.</li>')
        } else if (rateChange && !clearingRate) {
          // XOL-514: `!clearingRate` guard. Clearing the rate nulls
          // selectedVersionId (loadVersionsForRate's `!rateId` branch), which
          // makes versionChange true and lands here — promising a pin to "the
          // new rate's current published version" when there is no new rate
          // and commitFinance sends no version at all.
          //
          // XOL-497: was "will follow the current (live) rate again", true only
          // while commitFinance sent an explicit rate_version_id: null to
          // unpin. It now OMITS the key (API XOL-507 422s an explicit null),
          // and SharerController::update() auto-pins currentForRate() on a rate
          // change — so the sharer ends up PINNED, not following the live rate.
          // Reachable without `clearable`: selectedVersionId is null after a
          // failed versions GET, or when the new rate has versions but none
          // is_current.
          items.push('<li>This sharer will be pinned to the new rate’s current published version; its fee recomputes in the background.</li>')
        }
      }
      return '<p>Before saving, please note:</p><ul style="margin:0;padding-left:20px;text-align:left;">' + items.join('') + '</ul>'
    },

    // XOL-259: fold the re-pin response into local details so the fee display refreshes
    onVersionRepinned: function (licensee) {
      if (!licensee) return
      if (licensee.calculatedRate !== undefined) {
        this.details.calculatedRate = licensee.calculatedRate
      }
      if (licensee.currentRate !== undefined) {
        this.details.currentRate = licensee.currentRate
      }
      if (licensee.rateVersion !== undefined) {
        this.details.rateVersion = licensee.rateVersion
      }
      // XOL-373: was four inlined lines here; now the shared fold, so the
      // whole-object save, the re-pin and both override paths cannot drift
      // apart again.
      this.applyStamp(licensee)
    },

    editState: function () {
      if (this.editing) {
        this.details.started = this.formatDateSql(this.details.started)
        this.details.startBilling = this.formatDateSql(this.details.startBilling)
        this.details.expiry = this.formatDateSql(this.details.expiry)
        this.details.terminated = this.formatDateSql(this.details.terminated)

        // XOL-467: mirror LicenseeRequest::withValidator()'s own early
        // return — the API only checks the chain when the save actually
        // TOUCHES a date. Skipping validateDates() here when nothing dirty-
        // gated changed is what lets a pure rate/override edit save on the
        // ~72 Active licensees that already violate the chain (an Agreement
        // Start, no Handover) — the dirty-gate's whole reason for existing.
        //
        // Blocking, field-level, above BOTH exits below — the early
        // `commitFinance` a few lines down (when `needsConfirm` is false) and
        // the `.then(commitFinance)` after the fee-change confirm. A
        // date-only edit sets every fee flag false and leaves by the early
        // exit, so gating only the confirm path would let it bypass
        // validation entirely — the exact edit this exists to catch.
        //
        // XOL-626: never in step 3. Those four inputs are hidden there (the sharer form owns the
        // dates) and the embedded payload never sends them, so a block would name a field the
        // user cannot see.
        if (!this.embedded && this.chainFieldsDirty() && !this.validateDates()) return

        var overrideChanged = this.details.overrideEnabled !== this.originalOverride
        var rateChanged = Number(this.details.currentRate) !== Number(this.originalRate)
        // XOL-514: normalise both sides before comparing. Element's clearable
        // el-select emits '' rather than null, so a raw `'' !== null` reported
        // a RATE CHANGE for a licensee that has no rate and still has no rate —
        // and then showed it the "fees will be calculated from the new rate's
        // tiers" confirm, describing a change that is not happening.
        //
        // Not reachable from the UI today (Element hides the clear icon when
        // there is no value to clear), which is exactly why it would have sat
        // there: found by a test, not by clicking. `|| null` also folds
        // undefined, so a payload that omits the key compares equal to one
        // carrying an explicit null.
        var currentRateId = this.details.rate_id || null
        var rateIdChanged = currentRateId !== (this.originalRateId || null)
        var versionIdChanged = this.canSwitchVersion && (this.selectedVersionId !== this.originalVersionId)

        var overrideOn = this.details.overrideEnabled && (overrideChanged || rateChanged)
        var overrideOff = !this.details.overrideEnabled && overrideChanged
        var rateChange = rateIdChanged

        // XOL-514: a rate CLEAR, distinguished from a rate SWAP. Only true when
        // the licensee actually had a rate and now has none — an already-
        // rateless licensee echoing an empty picker is not clearing anything,
        // gets no dialog and no flag, and the API leaves it alone (738 such
        // rows on the current baseline).
        //
        // Truthiness, not `=== null`: Element's `clearable` el-select emits
        // '' on clear, not null.
        var clearingRate = !!this.originalRateId && !this.details.rate_id

        var needsConfirm = overrideOn || overrideOff || rateChange || versionIdChanged

        if (!needsConfirm) {
          this.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged, clearingRate)
          return
        }

        var self = this
        this.$confirm(
          this.buildImpactMessage(overrideOn, overrideOff, rateChange, versionIdChanged, clearingRate),
          // XOL-514: a clear stops billing, so it gets its own title and verb
          // rather than sharing "Confirm fee change" with a swap. The two are
          // not the same decision and must not read as the same decision.
          clearingRate && !this.details.overrideEnabled ? 'Stop billing this sharer?' : 'Confirm fee change',
          {
            confirmButtonText: clearingRate && !this.details.overrideEnabled ? 'Remove rate and stop billing' : 'Save changes',
            cancelButtonText: 'Keep editing',
            center: true,
            dangerouslyUseHTMLString: true,
            type: 'warning'
          }
        ).then(function () {
          self.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged, clearingRate)
        }).catch(function () {
          // cancel: write nothing, stay in edit mode
        })
      } else {
        this.editing = true
        this.resumingFee = false
        this.clearDateErrors()
        this.autoFilled = false
        // XOL-306: seed the version selector when entering edit mode
        if (this.details.rate_id) {
          this.loadVersionsForRate(this.details.rate_id, false)
        }
      }
    },

    // XOL-467: field -> message for `dateChainErrors()`. This tab has no
    // `handover` input — the chain's ROOT rule reroutes onto Agreement
    // Start, the field whose presence made the requirement true, naming
    // where to actually fix it. Site → Financials cannot repair its own
    // block; the dirty-gate is what removes the urgency (an unrelated rate
    // change still saves).
    dateChainCopy: function (item) {
      switch (item.code) {
        case 'handover_required':
          return {
            field: 'started',
            message: HANDOVER_REQUIRED_MESSAGE
          }
        case 'started_required':
          return {field: 'started', message: 'Required when Billing Start is set.'}
        case 'billing_start_required':
          return {field: 'startBilling', message: 'Required when Expiry is set.'}
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.formatDate(this.details.started) + ').'
          }
        case 'expiry_before_billing_start':
          return {field: 'expiry', message: 'Must be after Billing Start.'}
        default:
          return null
      }
    },

    // XOL-467: true when this save would actually SEND one of the three date
    // keys commitFinance() dirty-gates (`handover` is never sent from this
    // tab at all). Mirrors LicenseeRequest::withValidator()'s own "touches no
    // date" early return.
    chainFieldsDirty: function () {
      return this.details.started !== this.originalStarted ||
        this.details.startBilling !== this.originalStartBilling ||
        this.details.expiry !== this.originalExpiry
    },

    // XOL-467: synchronous, hand-rolled — Element's own validate() is
    // unusable here (`v-model="details"`, not `:model`), and this form's
    // four date-inputs are keyless `v-if` siblings that get patch-reused on
    // a status flip (the XOL-451 always-passes trap Element's validate()
    // would fall into).
    validateDates: function () {
      var self = this
      var errors = dateChainErrors(this.details)
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.fieldErrors = map

      var fields = Object.keys(map)
      if (fields.length > 0) {
        this.notifyBlockedSave(fields.length)
        this.focusDateField(fields[0])
        return false
      }

      return true
    },

    // Errors clear on ANY date @change — the whole map, since the rules are
    // cross-field. Also checks for Agreement Start <-> Billing Start
    // auto-fill (XOL-467) — `expiry` changes route through here too but are
    // never a member of DATE_CHAIN_FIELDS, so they no-op in maybeAutoFillDates.
    onDateChange: function (field, value) {
      this.clearDateErrors()
      if (!this.editing) return
      this.maybeAutoFillDates(field, value)
    },

    // XOL-467 (any-to-all widening): Agreement Start and Billing Start can
    // each source the other — this tab has no Handover input. Fires only
    // when the other field was empty before this edit — the same gate
    // doubles as the "do not fight the user" re-arm condition (see
    // autoFillDates()'s own doc in licenseeDates.js). Mutates `details`
    // directly, so a fill IS a genuine edit `chainFieldsDirty()` sees and
    // `commitFinance()` sends.
    maybeAutoFillDates: function (field, value) {
      var updates = autoFillDates(this.details, field, value, DATE_CHAIN_FIELDS)
      var keys = Object.keys(updates)
      if (keys.length === 0) return

      var self = this
      keys.forEach(function (key) { self.details[key] = updates[key] })
      this.autoFilled = true
    },

    // XOL-479: 422 backstop. validateDates() blocks everything the CLIENT can
    // see, but this tab's `details.handover` is a stale snapshot taken when
    // the component mounted — a concurrent edit on the Project tab (someone
    // else clears Handover between mount and Save) is invisible to it. The
    // API is the source of truth and re-checks the same chain
    // (LicenseeRequest::withValidator() in xolas-tms-api), returning the
    // error keyed on `handover` (its own root field — this tab reroutes onto
    // `started`, the same as the client-side path). PostMixins.send already
    // raises one $notify per Laravel validation error; this only adds the
    // SAME actionable affordance the client-side block shows, keyed off the
    // same message constant so `showHandoverAction` lights up either way.
    onSaveErrors: function (errors) {
      if (!errors || !errors.handover) return
      this.fieldErrors = Object.assign({}, this.fieldErrors, { started: HANDOVER_REQUIRED_MESSAGE })
      this.focusDateField('started')
    },

    // XOL-479: navigate to this licensee's Project tab (the only screen with
    // a Handover input) without losing the in-progress Financials edit.
    // `licensee` stacks as an L2 drawer ON TOP of the L1 `site` drawer this
    // component lives inside (LicenseeDetailLayer, drawerRegistry.js) — the
    // L1 route/component identity is unchanged by pushDrawer, so THIS
    // component is not unmounted and `details`/`editing` survive naturally.
    // Verified in src/components/atoms/layers/DrawerStack.vue: the layer-host
    // v-for is keyed on `entry.name + ':' + entry.args.id`, which does not
    // change when an L2 entry is appended.
    openHandoverOnProjectTab: function () {
      if (!this.sharer || !this.sharer.id || !this.$router) return
      pushDrawer(this.$router, { open: { licensee: { id: this.sharer.id, tab: 'project' } } })
    }
  },

  watch: {
    // XOL-671: opening a fee tab bumps its key, so the panel is re-created and re-fetches.
    // Runs before the re-render, so a first open of the lazy pane fetches once, not twice.
    menu: function (val) {
      if (val === 'feeSchedule') this.feeScheduleOpens++
      if (val === 'feeTimeline') this.feeTimelineOpens++
    },

    details: function (data) {
      this.details = data
    },

    // XOL-626: step 3 needs to know when this form is being edited (one editor at a time).
    editing: function (v) {
      this.$emit('editing-change', v)
    },

    // The close-time dirty check; always false on the site page.
    embeddedDirty: function (v) {
      this.drawerDirty = v
    },

    editBlocked: function () {
      var self = this
      this.$nextTick(function () { self.tryFocusEdit() })
    },

    // Step 3 tells step 3's other consumers `saving` too (aria-busy on the Sharer Finance group).
    saving: function (v) {
      this.$emit('saving-change', v)
    },

    // The fresh row after a Cancel that followed a partial save (see cancelEmbedded).
    sharer: function () {
      if (!this.resyncPending) return

      this.resyncPending = false
      this.resyncFromSharer()
    }
  },

  data: function () {
    var derived = this.deriveOverride(this.sharer)
    return {
      menu: 'agreement',
      // XOL-671: per-tab open counters; the fee panels' :key, so each open is a fresh instance.
      feeScheduleOpens: 0,
      feeTimelineOpens: 0,
      editing: this.editMode,
      currency: settings.currency,
      details: Object.assign(this.clone(this.sharer), { overrideEnabled: derived }),
      originalRate: this.sharer.currentRate,
      originalOverride: derived,
      originalRateId: this.sharer.rate_id,
      // XOL-364: coerce to Number — the bulk-sharers payload serializes
      // rate_version_id as a string ("4"), but SelectRateVersions' options have
      // numeric ids and el-select matches by strict equality, so a string pin
      // matches no option and the selector renders the raw id instead of the label.
      originalVersionId: (this.sharer.rate_version_id != null && this.sharer.rate_version_id !== '') ? Number(this.sharer.rate_version_id) : null,
      // XOL-467: dirty-gate baselines for the date chain — see commitFinance().
      originalStarted: this.sharer.started,
      originalStartBilling: this.sharer.startBilling,
      originalExpiry: this.sharer.expiry,
      // XOL-695: the status baseline — a status save queues a server recompute (see commitFinance)
      originalStatus: this.sharer.status,
      // XOL-467: any-to-all auto-fill notice flag — see maybeAutoFillDates().
      autoFilled: false,
      rateVersions: [],
      selectedVersionId: null,
      loadingVersions: false,
      resumingAuto: false,
      resumingFee: false,
      billingIssues: [],
      // XOL-626 (embedded): a Save is several writes; `saving` holds until they all answered.
      saving: false,
      pendingWrites: 0,
      writesFailed: false,
      snapshot: null,
      savedSinceEdit: false,
      resyncPending: false,
      wantEditFocus: false,
      formKey: 0
    }
  },

  mounted: function () {
    this.loadBillingIssues()
  }
}

</script>

<style scoped>
/* XOL-626: the embedded form's tab strip is hidden (one pane); only the OUTER header, so a nested
   tabs component inside the pane (if one is ever added) keeps its own. */
.finance-sharer--embedded > ::v-deep .el-tabs__header {
  display: none;
}

.finance-sharer__rate-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 0 0 8px;
}

.finance-sharer__rate-note {
  margin: 0 0 8px;
  font-size: 12px;
}

.edit-blocked-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}

.finance-sharer__elsewhere {
  font-size: 12px;
}

/* XOL-367: tighten the two-line "Override/Lock Rate" toggle label. Element's
   .el-form-item__label is line-height:40px, so two lines would be 40px taller
   than the single-line sibling labels — opening a large gap AND dropping the
   checkbox a row below the other inputs. line-height:20px makes the two lines
   occupy 40px (== one sibling line), so the gap tightens and the checkbox
   realigns with the Current Fees / Discount / Interest Rate inputs. */
.override-lock-label ::v-deep .el-form-item__label {
  line-height: 20px;
}

/* XOL-467: reserve room for the absolutely-positioned .el-form-item__error so
   a blocked-save message can never overlap the Sharer Rate row below.
   Applied via a parent-supplied class (not a bare ::v-deep .el-form-item rule
   — that leaks into every nested form, XOL-451) while editing, to every date
   field in the row so a message appearing/disappearing on one field never
   reflows the others. */
.licensee-date--editing {
  margin-bottom: 56px !important;
}

.licensee-date--editing ::v-deep .el-form-item__error {
  line-height: 1.4;
}

/* XOL-479: wraps the Agreement Start date-input so the "Open Project tab to
   set Handover" button can be positioned relative to it. Inline-block to
   match how el-form-item sits in this inline form (Element's own
   inline-form CSS), so wrapping it here does not change the row's flow. */
.handover-field {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

/* The button sits BELOW the (up to 2-line) .el-form-item__error, inside the
   reserved gutter — .licensee-date--editing's 56px is tuned for the error
   text alone, so this field gets a taller reserve on top of it whenever the
   affordance is showing, extended enough that the button never overlaps the
   Sharer Rate row beneath (XOL-467's original overlap concern). */
.licensee-date--handover-gutter {
  margin-bottom: 94px !important;
}

.handover-field__action {
  position: absolute;
  left: 0;
  /* Centred in the band between the label text and the input's top border.
     Measured, not guessed: the label's glyph box ends 28px down the wrapper
     and the input starts at 50px, so the band is 22px for a 15px-tall button
     — only 7px of slack. 32px centres it: 4px clear of each.
       top:38  -> bottom 53, 3px INSIDE the input (sat on the border)
       top:28  -> flush against the label text, 0px above it
       top:32  -> 4px below the label, 4px above the input
     If the label's type size changes, re-measure the glyph box (a Range over
     the label's contents) rather than the padded element box — the element is
     50px tall and reading that is what produced the first two wrong values. */
  top: 32px;
  padding: 0;
}
</style>
